/****************************************************************************************
 * RecordingDetectionsSVG.ts
 * -----------------------------------------------------------------------------
 * Lightweight class that turns one JSON “recording” object into
 * an inline-SVG string.  Import it, feed it the data, drop the string into the DOM (or
 * wrap in a React component—see bottom).
 *
 *	Usage (React)
 *	─────────────
 *		import { RecordingDetectionsSVG, RecordingSVG } from './RecordingDetectionsSVG';
 *		…
 *		<RecordingSVG record={recordObj} />;
 ****************************************************************************************/

// ────────────────────────────────────────────────────────────────────────────────
// CONFIG (edit freely)
// ────────────────────────────────────────────────────────────────────────────────
const PX_PER_SEC = 1; //0.5;					// 2 s = 1 px  (vertical)
const TIMELINE_X = 60;					// x-pos of the vertical axis
const CONF_WIDTH = 60;				// width of the music confidence bar (horizontal)
const TOP_PAD = 20;
const LAYER_PAD = 3;					// gap between coloured bars
const RECT_W = 14;					// width of each detection bar
const DET_START_X = TIMELINE_X + CONF_WIDTH + LAYER_PAD;
const LABEL_START_X = DET_START_X + 5 * (RECT_W + LAYER_PAD) + 10;	// x-pos of labels
const LABEL_PAD = 45;				// gap between label and bar

const COLORS = {
	timeline: '#000000',
	conf: '#9dd8ff',
	watermark: '#800080',
	aft: '#800080',
	nfp: '#d62828',
	nfpLR: '#f47c00',
	nfp2s: '#f1fa8c',
	nfpLR2s: '#00ff00'
};

// display filter rule for NFP results – customize here
const nfpResultsFilter = (a: any) => {

	// get significant matches with unique .time values
	if (!a.significantMatches || !a.significantMatches.length) {
		return false;
	}
	const uniqueTimes = new Set(a.significantMatches.map((m: any) => m.time));
	if (uniqueTimes.size < 2) {
		return false;
	}

	// filter significant matches with a significantMatch.significantMatchesAtTime > 30
	// const significantMatchesAtTime = a.significantMatches.filter((m: any) => m.significantMatchesAtTime < 30);
	// if (significantMatchesAtTime.length < 2) {
	// 	return false;
	// }

	return a.significantMatches?.length ?? 0 > 1;
}

// ────────────────────────────────────────────────────────────────────────────────
// CLASS
// ────────────────────────────────────────────────────────────────────────────────
export default class RecordingDetectionsSVG {
	private durationSec = 1800;					// fixed 30 min

	constructor(
		private record: any,
		private pxPerSec = PX_PER_SEC
	) { }

	// public façade
	build(): string {
		const colLabels = ['DIGI', 'PINE', 'P455', 'TPUF', 'TP16'];	// column labels

		const h = TOP_PAD + this.y(this.durationSec);
		const w = TIMELINE_X + CONF_WIDTH + colLabels.length * (RECT_W + LAYER_PAD) + 350;

		let out: string[] = [
			`<svg xmlns = "http://www.w3.org/2000/svg" width = "${w}" height = "${h + 40}" font - family="sans-serif" > `,
			this.musicConfidencePolygon(h),
			this.line(TIMELINE_X, TOP_PAD, TIMELINE_X, h, COLORS.timeline, 2)
		];

		// minute ticks
		for (let s = 0; s <= this.durationSec; s += 60) {
			const y = this.y(s) + TOP_PAD;
			out.push(
				this.line(TIMELINE_X - 4, y, TIMELINE_X, y),
				this.text(TIMELINE_X - 36, y, String(s), 'end')
			);
		}

		// column labels
		for (let i = 0; i < colLabels.length; i++) {
			const x = RECT_W + DET_START_X + i * (RECT_W + LAYER_PAD);
			out.push(this.text(x, TOP_PAD - 10, colLabels[i], 'middle', -45));
		}

		this.paintLayer(this.grabWatermarks(), 0, COLORS.watermark, out);
		//this.paintLayer(this.grabAft(), 1, COLORS.aft, out);
		//const matchesNFP = this.grabNfp();
		const matchesPine = this.grabNfp(this.record.nfpTest.pinecone_results ?? null);
		const matchesPine455 = this.grabNfp(this.record.nfpTest.pinecone455_results ?? null);
		const matchesTpuf = this.grabNfp(this.record.nfpTest.tpuf_results ?? null);
		const matchesTpuf16 = this.grabNfp(this.record.nfpTest.tpuf_16_results ?? null);
		// const matches2s = this.joinNFPMatches([
		// 	this.grabNfpTest('results2s'),
		// 	this.grabNfpTest('lrResults2s'),
		// ]);

		this.paintLayer(matchesPine, 1, COLORS.nfp, out);
		this.paintLayer(matchesPine455, 2, COLORS.aft, out);
		this.paintLayer(matchesTpuf, 3, COLORS.nfp2s, out);
		this.paintLayer(matchesTpuf16, 4, COLORS.nfpLR2s, out);

		out.push('</svg>');
		return out.join('\n');
	}

	// ───────── helpers ─────────
	private y = (s: number) => s * this.pxPerSec;

	private line = (x1: number, y1: number, x2: number, y2: number, c = COLORS.timeline, w = 1) =>
		`<line x1 = "${x1}" y1 = "${y1}" x2 = "${x2}" y2 = "${y2}" stroke = "${c}" stroke-width="${w}" style="z-index: 1;" /> `;

	private rect = (x: number, y: number, h: number, c: string, rx = 3, ry = 3) =>
		`<rect x = "${x}" y = "${y}" width = "${RECT_W}" height = "${h}" fill = "${c}" rx="${rx}" ry="${ry}" stroke="white" stroke-width="1" /> `;

	private text = (x: number, y: number, txt: string, anchor = 'start', rotate = 0) =>
		`<text x="${x}" y="${y}" font-size="10" text-anchor="${anchor}" dominant-baseline="middle" transform="rotate(${rotate} ${x} ${y})" style="z-index: 10;" fill="black" stroke="white" stroke-width="2.5" paint-order="stroke" stroke-linejoin="round"> ${txt} </text>`;

	private musicConfidencePolygon(h: number): string {
		if (!this.record.musicConfidence || !this.record.musicConfidence.length) {
			return '';
		}
		const pts = this.record.musicConfidence.map(
			(mc: any) => `${TIMELINE_X + mc.confidence * CONF_WIDTH},${this.y(mc.time) + TOP_PAD}`
		);
		pts.push(`${TIMELINE_X},${h}`, `${TIMELINE_X},${TOP_PAD}`);
		return `<polygon points="${pts.join(' ')}" fill="${COLORS.conf}" opacity="0.7"/>`;
	}

	private paintLayer(list: any[], idx: number, color: string, out: string[]) {
		const x = DET_START_X + idx * (RECT_W + LAYER_PAD);
		const gap = 15; // gap between labels
		let lastLblY = -1e9; // running “previous label” position		

		list.forEach(m => {
			const yBar = this.y(m.start) + TOP_PAD;
			const h = Math.max(this.y(m.end - m.start), 2);

			// ─ detection bar
			out.push(this.rect(x, yBar, h, m.raw?.classification == 1 ? '#CCCCCC' : color));

			// ─ label placement
			let yLbl = yBar;
			// if (yLbl - lastLblY < gap) yLbl = lastLblY + gap;			// nudge down
			// lastLblY = yLbl;

			let xLbl = LABEL_START_X + (idx * LABEL_PAD);
			const xLine = xLbl - 2; // x-pos of the label line
			if (yLbl - lastLblY < gap) xLbl += gap * 1.5;			// nudge right
			lastLblY = yLbl;

			// if the m.id has a period in it, strip that and everything after
			if ((m.id + '').includes('.')) {
				m.id = m.id.split('.')[0];
			}

			// if the m.id has a period in it, strip that and everything after
			if ((m.id + '').includes('.')) {
				m.id = m.id.split('.')[0];
			}

			const mJsonString = JSON.stringify(m).replace(/"/g, '&quot;'); // Escape double quotes for the attribute
			out.push(
				this.line(x + RECT_W, yBar, xLine, yLbl, color),	// leader line
				`<text transform="rotate(30 ${xLbl} ${yLbl})" x="${xLbl}" y="${yLbl}" font-size="10" onclick="console.log(JSON.parse('${mJsonString}'))" style="cursor: pointer;">${m.id} (${(m.end - m.start).toFixed(0)})</text>`
			);
		});
	}


	private grabWatermarks() {
		return this.record.data.matches.map((m: any) => ({
			start: +m.start,
			end: +m.end,
			id: m.track?.raw?.file_id
		}));
	}
	private grabAft() {
		return (this.record.fingerprints?.fpDetections ?? []).map((d: any) => ({
			start: +d.start,
			end: +d.end,
			id: d.fileId
		}));
	}
	private joinNFPMatches(lists: any[]): any[] {
		// take first list and use as basis, then add or merge matches from other lists
		const matches: any[] = lists[0].map((m: any) => ({ ...m }));
		for (let i = 1; i < lists.length; i++) {
			const list = lists[i];
			list.forEach((m: any) => {
				const existing = matches.find(mm => mm.id === m.id);
				if (existing) {
					// merge times
					existing.start = Math.min(existing.start, m.start);
					existing.end = Math.max(existing.end, m.end);
					existing.lrRaw = m.raw;
				} else {
					matches.push({ ...m });
				}
			});
		}
		// sort by start time
		matches.sort((a, b) => a.start - b.start);
		return matches;
	}

	private grabNfp(data: any = null) {
		if (!data && !this.record.nfp) return [];

		data = data || this.record.nfp  as any;

		if (!data || (!data.finalMatches && !data.final_matches)) return [];

		// figure out which final matches key
		const finals = data.finalMatches || data.final_matches;
		console.log('- finals:', finals);
		const matches = finals.map((m: any) => ({
			start: +m.start || +m.start_time || 0,
			end: +m.end || +m.end_time,
			id: m.fileId || m.file_id,
			raw: m
		}));

		return matches;
	}

	private grabNfpTest(nfpType: string) {
		const analyses = (nfpType == 'nfp' ? Object.values(this.record.nfp.results) : this.record.nfpTest?.[nfpType]?.songAnalyses) ?? [];
		const matches = analyses.filter(nfpResultsFilter).flatMap((a: any, i: number) =>
			(a.matchBoundaries ?? []).map((b: any, idx: number) => ({
				start: +b.startTime,
				end: +b.endTime,
				id: `${a.song.split('/').pop() || 'song'}-${i}-${idx}`,
				raw: a
			}))
		);

		// sort by start time
		matches.sort((a: any, b: any) => a.start - b.start);
		return matches;
	}
}
